import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import { Country } from '/@/modules/predefined/models/Country';
import { Jurisdiction } from '/@/modules/predefined/models/Jurisdiction';
import { DocumentCollection } from '/@/modules/document/models/DocumentCollection';
import { MarriageTerminationReason } from '@/modules/contact/enums/MarriageTerminationReason';
import { Contact } from '@/modules/contact/models/Contact';
import CountryService from '/@/modules/predefined/services/CountryService';
import JurisdictionService from '/@/modules/predefined/services/JurisdictionService';
import { fullMonthDayYearDateFormat } from '@/modules/core/app/helpers/DateHelper';
import { PredefinedCountry } from '@/modules/predefined/enums/PredefinedCountry';

export class ContactMarriageHistory extends BaseEntityModel {
  id?: number;
  spouseId?: number;
  otherSpouseId?: number;
  terminationReason?: string;
  divorceJudgementNumber?: string;
  courtDistrict?: string;
  countryId?: number;
  jurisdictionId?: number;
  area?: string;
  startDate?: Date;
  endDate?: Date;
  wasCommonLawPartner?: boolean;
  spouse: Contact | null;
  otherSpouse: Contact | null;
  country: Country | null;
  jurisdiction: Jurisdiction | null;

  documents: DocumentCollection;

  constructor(model: Partial<ContactMarriageHistory> = {}) {
    super(model);

    this.id = model.id;
    this.spouseId = model.spouseId;
    this.otherSpouseId = model.otherSpouseId;
    this.terminationReason = model.terminationReason;
    this.countryId = model.countryId;
    this.jurisdictionId = model.jurisdictionId;
    this.startDate = model.startDate;
    this.endDate = model.endDate;
    this.area = model.area;
    this.divorceJudgementNumber = model.divorceJudgementNumber;
    this.courtDistrict = model.courtDistrict;
    this.wasCommonLawPartner = model.wasCommonLawPartner;

    this.spouse = Contact.mapModelOrNull(model.spouse);
    this.otherSpouse = Contact.mapModelOrNull(model.otherSpouse);
    this.country = Country.mapModelOrNull(model.country);
    this.jurisdiction = Jurisdiction.mapModelOrNull(model.jurisdiction);
    this.documents = DocumentCollection.mapModel(model.documents ?? []);
  }

  getModelName(): ModelType {
    return ModelType.CONTACT_MARRIAGE_HISTORY;
  }

  isTerminationReasonDeathOfSpouse(): boolean {
    return this.terminationReason === MarriageTerminationReason.DEATH_OF_SPOUSE.key;
  }

  isTerminationReasonDivorce(): boolean {
    return this.terminationReason === MarriageTerminationReason.DIVORCE.key;
  }

  isCompleted(): boolean {
    return !!this.startDate && !!this.countryId && (!!this.jurisdictionId || !!this.area);
  }

  getJurisdictionName(): string {
    return JurisdictionService.getJurisdictionById(this.jurisdictionId)?.name ?? '';
  }

  getCountryName(): string {
    return CountryService.getCountryById(this.countryId)?.name ?? '';
  }

  getJurisdictionOrArea(): string | undefined {
    return PredefinedCountry.isActiveCountry(this.countryId)
      ? this.getJurisdictionName()
      : this.area;
  }

  get marriageDetails(): string {
    return `${this.startDate}, ${this.getCountryName()}, ${this.getJurisdictionOrArea()}`;
  }

  get formattedDateOfMarriage(): string {
    return fullMonthDayYearDateFormat(this.startDate);
  }

  get formattedDateOfTermination(): string {
    return fullMonthDayYearDateFormat(this.endDate);
  }

  get displayTerminationReason(): string {
    return MarriageTerminationReason.getByKey(this.terminationReason)?.value ?? '';
  }
}
